import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';
import Modal, { ModalProps } from '@app/component/overlays/modal/Modal';
import FormButton from '@app/component/form/button/FormButton';
import Message from '@app/component/message/Message';
import TablePlaceholder from '@app/component/placeholder/TablePlaceholder';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import { AbstractButton } from '@app/site/_form/button/AbstractButton';
import useAppForm from '@app/site/_form';
import Utilities from '@util/Utilities';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import PricingRoomWithPriceOutput from '@app/model/pricing-automation/output/PricingRoomWithPriceOutput';
import PricingIntegrationInput from '@app/model/pricing-automation/input/PricingIntegrationInput';
import ManualPricingIntegrationInput from '@app/model/pricing-automation/input/ManualPricingIntegrationInput';
import SubmitPricingResultOutput from '@app/model/pricing-automation/output/SubmitPricingResultOutput';
import SubmitMonthlyPricingResultOutputState from '@app/model/pricing-automation/output/SubmitMonthlyPricingResultOutputState';
import PricingAutomationService from '@app/model/pricing-automation/service/PricingAutomationService';
import { PricingAutomationQueryKeys } from '@app/model/pricing-automation/service/PricingAutomationQueryKeys';
import {
	useMarketStateTools,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import AvailabilityDailyStateProvider
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/provider/AvailabillityDailyStateProvider';
import PriceSubmissionBlockedItems from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/PriceSubmissionBlockedItems';
import BulkAdjustmentBar from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustmentBar';
import {
	applyBulkAdjustment,
	BulkAdjustmentType,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/bulk-adjustment/BulkAdjustment.utilities';
import PriceEditRoomsTable from './PriceEditRoomsTable';
import { createPriceEditPlan, PriceEditPlan, toManualPricingInput } from './PriceEdit.utilities';

type PriceEditModalProps = {
	companyHotel: CompanyHotel,
	statDate: string,
	// The newest states of the open tab; empty on a day with no state.
	recommendationStates: PriceRecommendationState[],
	architectures: PricingArchitecture[],
	// The architectures of a room-type tab; undefined lists every room.
	architectureIds?: number[],
	onSubmit: () => void,
} & Omit<ModalProps, 'title' | 'width' | 'actions'>

/**
 * Edit Prices (ROO2-313 R6): the one popup of the Day Pricing drawer, on every day. Current is the live WebHotelier
 * price. With an open valid recommendation it sends a Recommendation Submit, else a manual submit (createPriceEditPlan).
 */
const PriceEditModal = React.memo(function PriceEditModal({
	companyHotel,
	statDate,
	recommendationStates,
	architectures,
	architectureIds,
	onSubmit,
	...props
}: PriceEditModalProps) {
	const formId = React.useId();
	const [canSubmit, setCanSubmit] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	// The Reset of the footer reaches the form inside the modal through this counter.
	const [resetEpoch, setResetEpoch] = React.useState(0);
	const title = `Edit Prices - ${Utilities.formatDateString(statDate, 'DD MMMM YYYY', false)}`;

	return <Modal title={title}
		width="2xlarge"
		{...props}
		onOpen={() => {
			setCanSubmit(false);
			setIsSubmitting(false);
			setResetEpoch(0);
		}}
		actions={<>
			<AbstractButton state="text" type="button" disabled={!canSubmit || isSubmitting}
				onClick={() => setResetEpoch(epoch => epoch + 1)}>
				Reset
			</AbstractButton>
			<FormButton state="primary" type="submit" form={formId} disabled={!canSubmit || isSubmitting} isLoading={isSubmitting}>
				Submit
			</FormButton>
		</>}>
		<PriceEditContents companyHotel={companyHotel}
			statDate={statDate}
			recommendationStates={recommendationStates}
			architectures={architectures}
			architectureIds={architectureIds}
			formId={formId}
			resetEpoch={resetEpoch}
			onClose={() => props.ref.current?.close()}
			onSubmit={onSubmit}
			onCanSubmitChange={setCanSubmit}
			onSubmittingChange={setIsSubmitting} />
	</Modal>;
});

const UnpricedRoomsList: React.FunctionComponent<{ unpricedRooms: PricingRoomWithPriceOutput[] }> = ({ unpricedRooms }) =>
	<div>
		<ul className="mt-2 list-disc space-y-1 pl-5">
			{unpricedRooms.map(roomWithPrice => <li key={roomWithPrice.room.id}>
				{roomWithPrice.room.roomName}
				{roomWithPrice.room.rateName !== undefined && roomWithPrice.room.rateName !== null && roomWithPrice.room.rateName !== '' && ` (${roomWithPrice.room.rateName})`}
				{roomWithPrice.isAnchor && ': its group is not shown, because this is the main room of the group.'}
			</li>)}
		</ul>
		<p className="mt-2">Add a price for these rates in WebHotelier, or select a different rate in Pricing Architecture.</p>
	</div>;

type PriceEditContentsProps = {
	companyHotel: CompanyHotel,
	statDate: string,
	recommendationStates: PriceRecommendationState[],
	architectures: PricingArchitecture[],
	architectureIds?: number[],
	formId: string,
	resetEpoch: number,
	onClose: () => void,
	onSubmit: () => void,
	onCanSubmitChange: (canSubmit: boolean) => void,
	onSubmittingChange: (isSubmitting: boolean) => void,
};

const PriceEditContents: React.FunctionComponent<PriceEditContentsProps> = ({
	companyHotel, statDate, recommendationStates, architectures, architectureIds, onCanSubmitChange, ...props
}) => {
	const { t } = useTranslation();
	const tools = useMarketStateTools();
	// The live prices are read again each time the popup opens: the form starts from them. No refetch on window focus
	// or reconnect: it would replace the form and lose the New Rates the user typed (for example after a look at
	// WebHotelier in another tab).
	const { data, isFetching, isSuccess, isError, error } = useQuery({
		queryKey: PricingAutomationQueryKeys.pricingRoomsWithPrice(companyHotel.id, statDate),
		queryFn: () => PricingAutomationService.findCompanyHotelPricingRoomWithPrice(companyHotel.id, statDate),
		refetchOnMount: 'always',
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	});
	const submittedStateIds = tools.findDailySnapshotState(statDate)?.marketStateDailySnapshot?.submittedRecommendationStateIds;

	const plan = React.useMemo(() => isSuccess ? createPriceEditPlan({
		statDate,
		states: recommendationStates,
		architectures,
		liveRooms: data ?? [],
		submittedStateIds: submittedStateIds ?? [],
		canSubmitRecommendation: companyHotel.hasRecommendationCanSubmitPrices() === true,
		architectureIds,
	}) : undefined, [isSuccess, statDate, recommendationStates, architectures, data, submittedStateIds, companyHotel, architectureIds]);

	const hasRooms = plan !== undefined && plan.groups.length > 0;
	React.useEffect(() => {
		onCanSubmitChange(!isFetching && hasRooms);
	}, [isFetching, hasRooms, onCanSubmitChange]);

	if (isFetching) return <TablePlaceholder />;

	// The executor rejects with a string (the error code) or with an Error: show the one that has text.
	if (isError) return <Message state="error">{typeof error === 'string' ? t(error) : (error as Error | null)?.message ?? 'Unknown error'}</Message>;

	if (plan === undefined) return <Message state="error">Room configuration has changed. Reload the page before editing these prices.</Message>;

	if (!hasRooms) {
		if (_isEmpty(plan.unpricedRooms)) return <Message state="error">No prices found</Message>;
		return <Message state="error" title={<strong>No prices found</strong>}>
			<UnpricedRoomsList unpricedRooms={plan.unpricedRooms} />
		</Message>;
	}

	return <AvailabilityDailyStateProvider companyHotel={companyHotel} statDate={statDate}>
		{!_isEmpty(plan.unpricedRooms) && <Message state="warning"
			title={<strong>Some rooms have no price in WebHotelier for {Utilities.formatDateString(statDate, 'DD MMMM YYYY', false)}. You cannot submit them here.</strong>}>
			<UnpricedRoomsList unpricedRooms={plan.unpricedRooms} />
		</Message>}
		<PriceEditForm companyHotel={companyHotel} statDate={statDate} plan={plan} {...props} />
	</AvailabilityDailyStateProvider>;
};

const PriceEditForm: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	statDate: string,
	plan: PriceEditPlan,
	formId: string,
	resetEpoch: number,
	onClose: () => void,
	onSubmit: () => void,
	onSubmittingChange: (isSubmitting: boolean) => void,
}> = ({ companyHotel, statDate, plan, formId, resetEpoch, onClose, onSubmit, onSubmittingChange }) => {
	const toaster = useToaster();
	const { t } = useTranslation();
	const tools = useMarketStateTools();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [submissionResult, setSubmissionResult] = React.useState<SubmitPricingResultOutput | null>(null);
	const [derivativeSynchronizationSuppressionEpoch, setDerivativeSynchronizationSuppressionEpoch] = React.useState(0);
	const submissionError = submissionResult?.errorCode ?? submissionResult?.errorMessage;

	const finish = () => queryClient.invalidateQueries({
		queryKey: PricingAutomationQueryKeys.pricingSubmissions(companyHotel.id),
	}).then(() => {
		toaster.success('Pricing', 'Pricing submitted successfully!');
		tools.reloadMarketStateForDay(statDate);
		onClose();
		onSubmit();
	});

	// A Recommendation Submit: blocked items and errors stay in the popup.
	const recommendationMutation = useMutation({
		mutationFn: PricingAutomationService.submitPricing,
		onMutate: () => setSubmissionResult(null),
		onSuccess: result => {
			if (result.state === SubmitMonthlyPricingResultOutputState.FAILED) {
				setSubmissionResult(result);
				return;
			}
			return finish();
		},
		onError: (e: string) => toaster.error('Pricing', t(e)),
	});

	const manualMutation = useMutation({
		mutationFn: PricingAutomationService.submitManualPricing,
		onSuccess: () => finish(),
		onError: (e: string) => toaster.error('Pricing', t(e)),
	});

	const isPending = recommendationMutation.isPending || manualMutation.isPending;
	React.useEffect(() => {
		onSubmittingChange(isPending);
	}, [onSubmittingChange, isPending]);

	const form = useAppForm({
		defaultValues: plan.defaultValues,
		onSubmit: async ({ value }) => {
			if (plan.mode === 'recommendation') {
				return recommendationMutation.mutateAsync({
					companyHotelId: companyHotel.id,
					input: value as PricingIntegrationInput,
				}).catch(() => undefined);
			}
			return manualMutation.mutateAsync({
				companyHotelId: companyHotel.id,
				input: toManualPricingInput(value as ManualPricingIntegrationInput),
			}).catch(() => undefined);
		},
	});

	// The Reset of the footer: every New Rate back to its start value, no cascade, the bar cleared.
	const previousResetEpoch = React.useRef(resetEpoch);
	React.useEffect(() => {
		if (previousResetEpoch.current === resetEpoch) return;
		previousResetEpoch.current = resetEpoch;
		setDerivativeSynchronizationSuppressionEpoch(epoch => epoch + 1);
		setSubmissionResult(null);
		form.reset();
	}, [form, resetEpoch]);

	// The Bulk Adjustment: every New Rate becomes the Current Rate (the live price) plus the change, no cascade.
	const applyBulk = (adjustmentType: BulkAdjustmentType, adjustmentValue: number) => {
		setDerivativeSynchronizationSuppressionEpoch(epoch => epoch + 1);
		plan.groups.forEach(group => group.rooms.forEach(room => {
			const adjustedPrice = applyBulkAdjustment(room.currentPrice, adjustmentType, adjustmentValue);
			if (adjustedPrice === undefined) return;
			form.setFieldValue(`rates[${group.rateIndex}].roomPrices[${room.roomIndex}].submittedPrice`, adjustedPrice);
		}));
	};

	return <form.AppForm>
		<form id={formId} onSubmit={(e) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		}}>
			<p className="mb-2 text-sm text-gray-600">Set the rates for the rooms below and submit to update the rates.</p>

			{submissionResult !== null && submissionResult.blockedItems.length > 0 ? <PriceSubmissionBlockedItems
				items={submissionResult.blockedItems}
				currency={companyHotel.settings.main.currency}
				onDateSelect={selectedStatDate => {
					onClose();
					navigate('/secured/rateshopper/rates/market/calendar', { state: { selectedStatDate } });
				}} /> : null}
			{submissionResult !== null && submissionResult.blockedItems.length === 0 && submissionError !== undefined && submissionError !== '' &&
				<Message state="error">{t(submissionError, ...(submissionResult.errorParams ?? []))}</Message>}

			<BulkAdjustmentBar currency={companyHotel.settings.main.currency}
				onApply={applyBulk}
				resetEpoch={resetEpoch} />
			<PriceEditRoomsTable title="Rates Summary"
				currency={companyHotel.settings.main.currency}
				groups={plan.groups}
				form={form}
				suppressionEpoch={derivativeSynchronizationSuppressionEpoch} />
		</form>
	</form.AppForm>;
};

export default PriceEditModal;
